import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { SuspenseContentWrapper } from '@euler/app/components/SuspenseContentWrapper';
import { OrderDetailProvider } from '@euler/app/flows/order/components/OrderDetailProvider';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useFetchServiceRecord } from '@euler/app/flows/record/functions/useFetchServiceRecord';
import { useOrderDetailPropsFromServiceRecord } from '@euler/app/flows/record/functions/useOrderDetailPropsFromServiceRecord';
import { useCanRollbackOrder } from '@euler/app/flows/record/functions/useRollbackOrder';
import { ReportRef } from '@euler/app/flows/report/functions';
import { ErrorFallback } from '@euler/components/error';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { StoreConfiguration, VehicleServiceRecord } from '@euler/model';
import { WithExtraVehicleModelProps } from '@euler/model/WithExtraVehicleModelProps';
import { useWatchValue } from '@euler/utils/hooks';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type ServiceRecordContextProps = {
  serviceRecordId: string;
  loading: boolean;
  serviceRecord: WithExtraVehicleModelProps<VehicleServiceRecord>;
  storeConfiguration: StoreConfiguration;
  refreshing: boolean;
  reportTarget: ReportRef;
  canRollback: boolean;
  rollback: () => Promise<void>;
  refresh: (silence?: boolean) => Promise<void>;
};

export const ServiceRecordContext = createContext<ServiceRecordContextProps>(
  null as any,
);

export const useServiceRecordContext = () => useContext(ServiceRecordContext);

export const getServiceRecordResourceNamespace = (id: string) => {
  return `service-record:${id}`;
};

export const getServiceRecordDetailResourceId = (taskNo: string) => {
  return `${getServiceRecordResourceNamespace(taskNo)}:detail`;
};

export const ServiceRecordContextProvider = memo(
  ({
    serviceRecordId,
    children,
  }: {
    serviceRecordId: string;
    children?: ReactNode;
  }) => {
    const { state, loading, error, refreshing, invalidate } =
      useFetchServiceRecord(serviceRecordId);

    if (state) {
      return (
        <Content
          serviceRecord={state}
          loading={loading ?? false}
          refreshing={refreshing ?? false}
          invalidate={invalidate}
        >
          {children}
        </Content>
      );
    }

    if (error) {
      return <ErrorFallback error={error} />;
    }

    if (loading) {
      return <Loader />;
    }

    return null;
  },
);

const Content = memo(
  ({
    serviceRecord,
    loading,
    refreshing,
    invalidate,
    children,
  }: {
    serviceRecord: WithExtraVehicleModelProps<VehicleServiceRecord>;
    loading: boolean;
    refreshing: boolean;
    invalidate: (silence?: boolean) => Promise<void>;
    children?: ReactNode;
  }) => {
    const resourceId = getServiceRecordDetailResourceId(serviceRecord._id);
    const { rpx } = useResponsiveContext();
    const { navBarHeight } = useSystemMetrics();
    const { event$ } = useOrderContext();

    const reportTarget = useMemo<ReportRef>(
      () => ({
        type: 'report',
        id: serviceRecord.reportNo,
        orderNo: serviceRecord.orderNo,
        reportNo: serviceRecord.reportNo,
        licensePlateNo: serviceRecord.licensePlateNo,
        vehicleName: serviceRecord.vehicleName,
        vehicleImageUrl: serviceRecord.vehicleImageUrl,
      }),
      [
        serviceRecord.licensePlateNo,
        serviceRecord.orderNo,
        serviceRecord.reportNo,
        serviceRecord.vehicleImageUrl,
        serviceRecord.vehicleName,
      ],
    );

    const {
      canRollback,
      rollback,
      check: checkCanRollback,
    } = useCanRollbackOrder({
      orderNo: serviceRecord.orderNo,
      taskNo: serviceRecord.taskNo,
      finishedAt: serviceRecord.finishedAt,
      isMainOrder: serviceRecord.isMainRecord,
      allowed: serviceRecord.isMainRecord,
    });

    const refresh = useCallback(
      async (silence?: boolean) => {
        void invalidate(silence);
        void checkCanRollback();
      },
      [checkCanRollback, invalidate],
    );

    const subscribed = serviceRecord.subscribed;

    useWatchValue(subscribed, (prev, curr) => {
      if (!prev && curr) {
        event$.next({
          type: 'subscribed',
          orderNo: serviceRecord.orderNo,
          taskNo: serviceRecord.taskNo,
          isMainOrder: serviceRecord.isMainRecord,
        });
      } else if (prev && !curr) {
        event$.next({
          type: 'unsubscribed',
          orderNo: serviceRecord.orderNo,
          taskNo: serviceRecord.taskNo,
          isMainOrder: serviceRecord.isMainRecord,
        });
      }
    });

    const storeConfiguration = useStoreConfiguration();

    const value = useMemo<ServiceRecordContextProps>(
      () => ({
        storeConfiguration,
        serviceRecordId: serviceRecord._id,
        serviceRecord,
        loading,
        refreshing,
        refresh,
        reportTarget,
        canRollback,
        rollback,
      }),
      [
        storeConfiguration,
        serviceRecord,
        loading,
        refreshing,
        refresh,
        reportTarget,
        canRollback,
        rollback,
      ],
    );

    const orderDetailProps =
      useOrderDetailPropsFromServiceRecord(serviceRecord);

    return (
      <SuspenseContentWrapper
        resourceId={resourceId}
        contentContainerStyle={{
          paddingTop: navBarHeight + rpx(32),
        }}
      >
        <OrderDetailProvider props={orderDetailProps}>
          <ServiceRecordContext.Provider value={value}>
            {children}
          </ServiceRecordContext.Provider>
        </OrderDetailProvider>
      </SuspenseContentWrapper>
    );
  },
);

const Loader = memo(() => {
  const { top } = useSafeAreaInsets();
  const { navBarHeight, statusBarHeight } = useSystemMetrics();
  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        paddingTop: top + navBarHeight + statusBarHeight,
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      <DefaultLoader />
    </ScrollView>
  );
});
